// store/api/leadListsApi.ts
import { expectedOrganizationHeaders } from '@/store/expected-organization-headers';
import {
  LeadList,
  LeadListCompaniesResponse,
  LeadListLeads,
  LeadListShare,
  LeadListShareRole,
  LeadListVisibility,
  LeadWithProspect,
  ShareableUser,
  CompanyUploadItem,
  CompanyUploadResponse,
  FindPersonalEmailRequest,
  FindPersonalEmailJobResponse,
  EnrichCompaniesRequest,
  EnrichCompaniesJobResponse,
} from '@/types';
import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import posthog from 'posthog-js';
import { isInternalList } from '@/utils/internal-list.util';
import { baseQueryWithAuth } from '../base-query';

/** The grid reads only part of each lead's raw_enrichment, and the whole
 *  provider blob was ~70% of a leads page. While this is on, the API sends
 *  just that part (rows marked `raw_enrichment_partial`) and the lead drawer
 *  fetches the rest (`getLeadRawEnrichment`). Read as each request is built,
 *  without waiting: a page fetched before PostHog's flags load gets the full
 *  blob, which renders the same. */
export const LEADS_GRID_SLIM_RAW_FLAG = 'leads_grid_slim_raw_v1';

export interface CreateLeadListInput {
  name: string;
  description?: string;
  origin: string;
  visibility?: LeadListVisibility;
  /** Pins the create to this org: never sent in the body. */
  organizationId?: string;
}

export interface EditLeadListInput {
  name?: string;
  description?: string;
  visibility?: LeadListVisibility;
  auto_run_enabled?: boolean;
  auto_run_config?: Record<string, unknown>;
  crm_poll_enabled?: boolean;
  folder_id?: string | null;
}

export interface AssignLeadInput {
  listId: string;
  leadId: string;
}

export interface DeleteLeadListResponse {
  message: string;
  id: string;
}

export interface BulkDeleteLeadListsResponse {
  message: string;
  deleted_count: number;
  failed_count: number;
}

export interface BulkDeleteLeadsResponse {
  message: string;
  removed_count: number;
  remaining_count: number;
  not_found_count: number;
}

export interface FilterCondition {
  field: string;
  operator: string;
  value: string;
  logic?: 'and' | 'or';
}

export interface FilterLeadsRequest {
  filters: FilterCondition[];
  page: number;
  page_size: number;
}

export interface FilterLeadsResponse {
  leads: LeadWithProspect[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ── Cleanlist-level dedup exclusion (CP-8) ──
//
// The org's existing leads to leave out of a People search, sent as the
// ``cleanlist`` identity reference the server resolves. ``scope:'any'`` spans
// every lead in the org; ``scope:'list'`` is scoped to ``list_ids``.
// Discriminated union so ``list_ids`` is REQUIRED exactly when scope is 'list'
// (and absent for 'any') — invalid payloads fail at compile time, not runtime.
export type LeadListExclusionIdentifiersRequest =
  | { scope: 'any' }
  | { scope: 'list'; list_ids: string[] };

export const leadListsApi = createApi({
  reducerPath: 'leadListsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'LeadList',
    'LeadListLeads',
    'LeadListCompanies',
    'LeadListShare',
    'LeadListMembers',
  ],
  endpoints: (builder) => ({
    getLeadLists: builder.query<LeadList[], { organizationId: string } | void>({
      query: (args) =>
        args
          ? {
              url: '/lead-lists',
              headers: { 'X-Cleanlist-Expected-Org': args.organizationId },
            }
          : '/lead-lists',
      transformResponse: (lists: LeadList[]) =>
        Array.isArray(lists)
          ? lists.filter((list) => !isInternalList(list))
          : lists,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'LeadList' as const,
                id: id,
              })),
              { type: 'LeadList', id: 'LIST' },
            ]
          : [{ type: 'LeadList', id: 'LIST' }],
    }),

    getLeadListLeads: builder.query<
      LeadListLeads,
      {
        listId: string;
        page?: number;
        pageSize?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
      }
    >({
      query: ({ listId, page, pageSize, sortBy, sortOrder }) => {
        const params: Record<string, string | number> = {};
        if (typeof page === 'number') {
          params.page = page;
        }
        if (typeof pageSize === 'number') {
          params.page_size = pageSize;
        }
        // Only forward sort params for keys the backend can order the FULL
        // list by; the caller gates this so we never send an unsupported key.
        if (sortBy) {
          params.sort_by = sortBy;
          params.sort_order = sortOrder ?? 'asc';
        }
        if (
          posthog.__loaded &&
          posthog.isFeatureEnabled(LEADS_GRID_SLIM_RAW_FLAG) === true
        ) {
          params.raw_enrichment = 'grid';
        }
        return {
          url: `/lead-lists/${listId}/leads`,
          params: Object.keys(params).length > 0 ? params : undefined,
        };
      },
      providesTags: (_, __, { listId }) => [
        { type: 'LeadListLeads', id: listId },
        { type: 'LeadList', id: listId },
      ],
    }),

    // Every lead id on the list (plus page 1's raw-people ids), in the
    // grid's server order. Select-all reads this instead of every page.
    // Not cached: each select-all wants the list as it is now.
    getLeadListLeadIds: builder.query<
      { lead_ids: string[]; raw_person_ids: string[] },
      { listId: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }
    >({
      query: ({ listId, sortBy, sortOrder }) => ({
        url: `/lead-lists/${listId}/leads/ids`,
        params: sortBy
          ? { sort_by: sortBy, sort_order: sortOrder ?? 'asc' }
          : undefined,
      }),
      keepUnusedDataFor: 0,
    }),

    // One lead's full raw_enrichment, for the drawer when its grid row
    // carried only the grid's keys (`raw_enrichment_partial`).
    getLeadRawEnrichment: builder.query<
      { raw_enrichment: Record<string, unknown> | null },
      { listId: string; leadId: string }
    >({
      query: ({ listId, leadId }) =>
        `/lead-lists/${listId}/leads/${leadId}/raw-enrichment`,
      providesTags: (_, __, { listId }) => [
        { type: 'LeadListLeads', id: listId },
      ],
    }),

    getLeadListCompanies: builder.query<
      LeadListCompaniesResponse,
      {
        listId: string;
        page?: number;
        pageSize?: number;
        search?: string;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
      }
    >({
      query: ({
        listId,
        page = 1,
        pageSize = 50,
        search,
        sortBy,
        sortOrder,
      }) => {
        const params: Record<string, string | number> = {
          page,
          page_size: pageSize,
        };
        if (search) params.search = search;
        if (sortBy) params.sort_by = sortBy;
        if (sortOrder) params.sort_order = sortOrder;
        return {
          url: `/lead-lists/${listId}/companies`,
          params,
        };
      },
      providesTags: (_, __, { listId }) => [
        { type: 'LeadListCompanies', id: listId },
      ],
    }),

    createLeadList: builder.mutation<LeadList, CreateLeadListInput>({
      query: ({ organizationId, ...input }) => ({
        url: '/lead-lists',
        method: 'POST',
        body: input,
        ...(organizationId
          ? { headers: expectedOrganizationHeaders(organizationId) }
          : {}),
      }),
      invalidatesTags: [{ type: 'LeadList', id: 'LIST' }],
    }),

    // Get-or-create the user's protected "Extension Leads" list. Fail-open
    // fallback for reveal flows on orgs onboarded before the protected-list
    // webhook existed (their getLeadLists carries no is_protected entry).
    // Idempotent on the backend — safe to call repeatedly.
    getOrCreateDefaultLeadList: builder.mutation<LeadList, void>({
      query: () => ({
        url: '/lead-lists/default',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'LeadList', id: 'LIST' }],
    }),

    editLeadList: builder.mutation<
      LeadList,
      { listId: string; input: EditLeadListInput }
    >({
      query: ({ listId, input }) => ({
        url: `/lead-lists/${listId}`,
        method: 'PUT',
        // folder_id null = "no folder". Sent as '', which every API version
        // reads that way (JSON null was ignored before cleanse-api #1304).
        body: input.folder_id === null ? { ...input, folder_id: '' } : input,
      }),
      async onQueryStarted({ listId, input }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          leadListsApi.util.updateQueryData(
            'getLeadLists',
            undefined,
            (draft) => {
              const list = draft.find((l) => l.id === listId);
              if (list) {
                if (input.folder_id !== undefined)
                  list.folder_id = input.folder_id ?? undefined;
                if (input.name !== undefined) list.name = input.name;
                if (input.description !== undefined)
                  list.description = input.description;
                if (input.visibility !== undefined)
                  list.visibility = input.visibility;
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadList', id: listId },
        { type: 'LeadList', id: 'LIST' },
        { type: 'LeadListLeads', id: listId },
      ],
    }),

    deleteLeadList: builder.mutation<DeleteLeadListResponse, string>({
      query: (listId) => ({
        url: `/lead-lists/${listId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, listId) => [
        { type: 'LeadList', id: listId },
        { type: 'LeadList', id: 'LIST' },
      ],
    }),

    bulkDeleteLeadLists: builder.mutation<
      BulkDeleteLeadListsResponse,
      string[]
    >({
      query: (listIds) => ({
        url: `/lead-lists/bulk-delete`,
        method: 'POST',
        body: { list_ids: listIds },
      }),
      invalidatesTags: [{ type: 'LeadList', id: 'LIST' }],
    }),

    assignLeadToList: builder.mutation<{ success: boolean }, AssignLeadInput>({
      query: ({ listId, leadId }) => ({
        url: `/lead-lists/${listId}/bind`,
        method: 'POST',
        body: { lead_id: leadId },
      }),
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadListLeads', id: listId },
        { type: 'LeadList', id: listId },
      ],
    }),

    // Say who a pending import row is (a suggested match or a pasted URL), or
    // null for "none of these". A pick starts the row's lookup by that URL;
    // the conversion reaches the list over the WebSocket like the import's.
    matchImportRow: builder.mutation<
      { status: 'started' | 'dismissed'; workflow_id?: string },
      { listId: string; rawPersonId: string; linkedinUrl: string | null }
    >({
      query: ({ listId, rawPersonId, linkedinUrl }) => ({
        url: `/lead-lists/${listId}/raw-people/${rawPersonId}/match`,
        method: 'POST',
        body: { linkedin_url: linkedinUrl },
      }),
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadListLeads', id: listId },
      ],
    }),

    // Same, for a lead Enrich Contact could not fill in: a pick runs Enrich
    // Contact on that one lead with the URL as its identity.
    matchListLead: builder.mutation<
      { status: 'started' | 'dismissed'; workflow_id?: string },
      { listId: string; leadId: string; linkedinUrl: string | null }
    >({
      query: ({ listId, leadId, linkedinUrl }) => ({
        url: `/lead-lists/${listId}/leads/${leadId}/match`,
        method: 'POST',
        body: { linkedin_url: linkedinUrl },
      }),
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadListLeads', id: listId },
      ],
    }),

    bulkDeleteLeads: builder.mutation<
      BulkDeleteLeadsResponse,
      { listId: string; leadIds: string[] }
    >({
      query: ({ listId, leadIds }) => ({
        url: `/lead-lists/${listId}/bulk-delete`,
        method: 'POST',
        body: { lead_ids: leadIds },
      }),
      async onQueryStarted(
        { listId, leadIds },
        { dispatch, getState, queryFulfilled }
      ) {
        const patches: { undo: () => void }[] = [];
        // A Set: a select-all delete sends every id on the list.
        const deleted = new Set(leadIds);
        const state = getState() as {
          [leadListsApi.reducerPath]: {
            queries: Record<
              string,
              { endpointName?: string; originalArgs?: { listId?: string } }
            >;
          };
        };

        const queries = state[leadListsApi.reducerPath]?.queries ?? {};
        Object.values(queries).forEach((entry) => {
          if (entry?.endpointName !== 'getLeadListLeads') return;
          if (!entry?.originalArgs?.listId) return;
          if (entry.originalArgs.listId !== listId) return;
          const originalArgs = entry.originalArgs as {
            listId: string;
            page?: number;
            pageSize?: number;
          };

          patches.push(
            dispatch(
              leadListsApi.util.updateQueryData(
                'getLeadListLeads',
                originalArgs,
                (draft) => {
                  let removedLeads = 0;
                  let removedRawPeople = 0;

                  if (draft?.leads?.length) {
                    const before = draft.leads.length;
                    draft.leads = draft.leads.filter(
                      (lead) => !deleted.has(lead.id)
                    );
                    removedLeads = before - draft.leads.length;
                  }

                  // Also remove from raw_people (CSV-imported stubs)
                  if (draft?.raw_people?.length) {
                    const before = draft.raw_people.length;
                    draft.raw_people = draft.raw_people.filter(
                      (rp: { id: string }) => !deleted.has(rp.id)
                    );
                    removedRawPeople = before - draft.raw_people.length;
                  }

                  const totalRemoved = removedLeads + removedRawPeople;
                  if (totalRemoved <= 0) return;

                  if (typeof draft.prospect_count === 'number') {
                    draft.prospect_count = Math.max(
                      0,
                      draft.prospect_count - totalRemoved
                    );
                  }
                  if (typeof draft.total_leads === 'number') {
                    draft.total_leads = Math.max(
                      0,
                      draft.total_leads - removedLeads
                    );
                  }
                  // pagination.total_count only tracks enriched leads, not raw_people
                  if (draft.pagination?.total_count != null) {
                    draft.pagination.total_count = Math.max(
                      0,
                      draft.pagination.total_count - removedLeads
                    );
                  }
                }
              )
            )
          );
        });

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadListLeads', id: listId },
        { type: 'LeadListCompanies', id: listId },
        { type: 'LeadList', id: listId },
        { type: 'LeadList', id: 'LIST' },
      ],
    }),

    bulkDeleteLeadsByCompany: builder.mutation<
      BulkDeleteLeadsResponse,
      { listId: string; companyIds: string[]; includeUnknown?: boolean }
    >({
      query: ({ listId, companyIds, includeUnknown = false }) => ({
        url: `/lead-lists/${listId}/bulk-delete-by-company`,
        method: 'POST',
        body: { company_ids: companyIds, include_unknown: includeUnknown },
      }),
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadListLeads', id: listId },
        { type: 'LeadListCompanies', id: listId },
        { type: 'LeadList', id: listId },
        { type: 'LeadList', id: 'LIST' },
      ],
    }),

    uploadCompaniesToList: builder.mutation<
      CompanyUploadResponse,
      { listId: string; companies: CompanyUploadItem[] }
    >({
      query: ({ listId, companies }) => ({
        url: `/lead-lists/${listId}/companies/upload`,
        method: 'POST',
        body: { companies },
      }),
      // The index's company_count changes with the sheet.
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadListCompanies', id: listId },
        { type: 'LeadList', id: 'LIST' },
      ],
    }),

    addCompanyToList: builder.mutation<
      CompanyUploadResponse,
      { listId: string; company: CompanyUploadItem }
    >({
      query: ({ listId, company }) => ({
        url: `/lead-lists/${listId}/companies/add`,
        method: 'POST',
        body: company,
      }),
      // The index's company_count changes with the sheet.
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadListCompanies', id: listId },
        { type: 'LeadList', id: 'LIST' },
      ],
    }),

    // Start find-personal-email job for selected leads. The job runs async
    // and streams progress over the leads WebSocket channel (personal_email_progress), so we don't
    // invalidate any tags here - the unified leads hook refetches on the
    // terminal frame once new personal emails have landed on the rows.
    findPersonalEmail: builder.mutation<
      FindPersonalEmailJobResponse,
      { listId: string; request: FindPersonalEmailRequest }
    >({
      query: ({ listId, request }) => ({
        url: `/lead-lists/${listId}/leads/find-personal-email`,
        method: 'POST',
        body: request,
      }),
    }),

    // Start bulk Enrich Company (firmographic) job. Backend auto-routes by
    // batch size: Wiza for <=2 companies, Crustdata CompanyDB for >=3.
    enrichCompaniesInList: builder.mutation<
      EnrichCompaniesJobResponse,
      { listId: string; request: EnrichCompaniesRequest }
    >({
      query: ({ listId, request }) => ({
        url: `/lead-lists/${listId}/companies/enrich`,
        method: 'POST',
        body: request,
      }),
    }),

    findOpenJobsInList: builder.mutation<
      EnrichCompaniesJobResponse,
      { listId: string; request: EnrichCompaniesRequest }
    >({
      query: ({ listId, request }) => ({
        url: `/lead-lists/${listId}/companies/find-open-jobs`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, _, { listId }) =>
        result?.status === 'completed'
          ? [{ type: 'LeadListCompanies', id: listId }]
          : [],
    }),

    findReviewsInList: builder.mutation<
      EnrichCompaniesJobResponse,
      { listId: string; request: EnrichCompaniesRequest }
    >({
      query: ({ listId, request }) => ({
        url: `/lead-lists/${listId}/companies/find-reviews`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, _, { listId }) =>
        result?.status === 'completed'
          ? [{ type: 'LeadListCompanies', id: listId }]
          : [],
    }),

    removeCompaniesFromList: builder.mutation<
      { removed_count: number },
      { listId: string; companyIds: string[] }
    >({
      query: ({ listId, companyIds }) => ({
        url: `/lead-lists/${listId}/companies/remove`,
        method: 'POST',
        body: { company_ids: companyIds },
      }),
      // The index's company_count changes with the sheet.
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadListCompanies', id: listId },
        { type: 'LeadList', id: 'LIST' },
      ],
    }),

    // Copy leads to another list (no credit charge)
    copyLeadsToList: builder.mutation<
      {
        message: string;
        copied_count: number;
        already_in_target: number;
        /** New people left out because the target list was at its cap. */
        over_cap?: number;
      },
      { sourceListId: string; targetListId: string; leadIds: string[] }
    >({
      query: ({ sourceListId, targetListId, leadIds }) => ({
        url: `/lead-lists/${sourceListId}/copy-leads`,
        method: 'POST',
        body: { lead_ids: leadIds, target_list_id: targetListId },
      }),
      invalidatesTags: (_, __, { targetListId }) => [
        { type: 'LeadListLeads', id: targetListId },
        { type: 'LeadListCompanies', id: targetListId },
        { type: 'LeadList', id: targetListId },
        { type: 'LeadList', id: 'LIST' },
      ],
    }),

    filterLeads: builder.mutation<
      FilterLeadsResponse,
      { listId: string; request: FilterLeadsRequest }
    >({
      query: ({ listId, request }) => ({
        url: `/lead-lists/${listId}/filter`,
        method: 'POST',
        body: request,
      }),
    }),

    normalizeLocations: builder.mutation<
      { updated_count: number; skipped_count: number; errors: string[] },
      { leadListId: string; leadIds?: string[] }
    >({
      query: ({ leadListId, leadIds }) => ({
        url: '/locations/normalize',
        method: 'POST',
        body: { lead_list_id: leadListId, lead_ids: leadIds },
      }),
      invalidatesTags: (_, __, { leadListId }) => [
        { type: 'LeadListLeads', id: leadListId },
        { type: 'LeadList', id: leadListId },
      ],
    }),

    getLeadListShares: builder.query<LeadListShare[], { listId: string }>({
      query: ({ listId }) => `/lead-lists/${listId}/shares`,
      providesTags: (_, __, { listId }) => [
        { type: 'LeadListShare', id: listId },
      ],
    }),

    getShareableUsers: builder.query<ShareableUser[], void>({
      query: () => '/lead-lists/shareable-users',
      providesTags: [{ type: 'LeadListMembers', id: 'LIST' }],
    }),

    createLeadListShare: builder.mutation<
      LeadListShare,
      { listId: string; userId: string; role: LeadListShareRole }
    >({
      query: ({ listId, userId, role }) => ({
        url: `/lead-lists/${listId}/shares`,
        method: 'POST',
        body: { user_id: userId, role },
      }),
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadListShare', id: listId },
      ],
    }),

    updateLeadListShare: builder.mutation<
      LeadListShare,
      { listId: string; userId: string; role: LeadListShareRole }
    >({
      query: ({ listId, userId, role }) => ({
        url: `/lead-lists/${listId}/shares/${userId}`,
        method: 'PATCH',
        body: { role },
      }),
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadListShare', id: listId },
      ],
    }),

    deleteLeadListShare: builder.mutation<
      { message: string; shared_with_user_id: string },
      { listId: string; userId: string }
    >({
      query: ({ listId, userId }) => ({
        url: `/lead-lists/${listId}/shares/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { listId }) => [
        { type: 'LeadListShare', id: listId },
      ],
    }),

    toggleFavorite: builder.mutation<
      { id: string; is_favorite: boolean },
      string
    >({
      query: (listId) => ({
        url: `/lead-lists/${listId}/favorite`,
        method: 'POST',
      }),
      async onQueryStarted(listId, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          leadListsApi.util.updateQueryData(
            'getLeadLists',
            undefined,
            (draft) => {
              const list = draft.find((l) => l.id === listId);
              if (list) {
                list.is_favorite = !list.is_favorite;
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    recordOpened: builder.mutation<
      { id: string; last_opened_at: string },
      string
    >({
      query: (listId) => ({
        url: `/lead-lists/${listId}/opened`,
        method: 'POST',
      }),
      async onQueryStarted(listId, { dispatch, getState, queryFulfilled }) {
        // Optimistic update — immediate UI feedback before the server responds
        const openedAt = new Date().toISOString();
        const patchResult = dispatch(
          leadListsApi.util.updateQueryData(
            'getLeadLists',
            undefined,
            (draft) => {
              const list = draft.find((l) => l.id === listId);
              if (list) list.last_opened_at = openedAt;
            }
          )
        );
        try {
          const { data } = await queryFulfilled;
          // Confirm with the server-returned timestamp so the cache matches
          // exactly what was committed to the DB. /opened returns naive UTC
          // (no offset), which the browser would read as local time.
          const serverAt = data.last_opened_at;
          const confirmedAt = serverAt
            ? /(Z|[+-]\d\d:?\d\d)$/i.test(serverAt)
              ? serverAt
              : `${serverAt}Z`
            : openedAt;
          const confirm = () =>
            dispatch(
              leadListsApi.util.updateQueryData(
                'getLeadLists',
                undefined,
                (draft) => {
                  const list = draft.find((l) => l.id === listId);
                  if (list) list.last_opened_at = confirmedAt;
                }
              )
            );
          confirm();
          // On a cold load the lists index can still be in flight, started
          // before this write, so it may land with the old timestamp. Wait for
          // that same request (no new fetch) and confirm again.
          const lists = leadListsApi.endpoints.getLeadLists.select()(
            getState() as Parameters<
              ReturnType<typeof leadListsApi.endpoints.getLeadLists.select>
            >[0]
          );
          if (lists.isLoading) {
            await dispatch(
              leadListsApi.endpoints.getLeadLists.initiate(undefined, {
                subscribe: false,
              })
            );
            confirm();
          }
        } catch {
          patchResult.undo();
        }
      },
      // No invalidatesTags: the cache patch above is the whole update.
      // Invalidating {LeadList, id} refetched the lists index AND page 1 of
      // the open list (getLeadListLeads provides the same tag) on every open.
    }),
  }),
});

export const applyLeadListTotalCount = (
  draft: LeadListLeads,
  totalCount?: number
) => {
  if (typeof totalCount !== 'number') return;

  if (draft.pagination) {
    draft.pagination.total_count = totalCount;
    if (draft.pagination.page_size) {
      draft.pagination.total_pages = Math.ceil(
        totalCount / draft.pagination.page_size
      );
    }
  }

  if (typeof draft.prospect_count === 'number') {
    draft.prospect_count = totalCount;
  }

  if (typeof draft.total_leads === 'number') {
    draft.total_leads = totalCount;
  }
};

export const updateLeadListLeadsCache = (
  dispatch: (action: unknown) => void,
  args: { listId: string; page?: number; pageSize?: number },
  updater: (draft: LeadListLeads) => void
) => {
  dispatch(
    leadListsApi.util.updateQueryData('getLeadListLeads', args, updater)
  );
};

type LeadListsRootState = {
  [K in typeof leadListsApi.reducerPath]: ReturnType<
    typeof leadListsApi.reducer
  >;
};

/**
 * A person a sync profile held is released: take `held_by` off every CRM entry
 * of that lead's `crm_sync_status` in every cached page of every lead list.
 * Releasing one person clears their hold on both CRMs, and a lead list's rows
 * read the status from the leads payload, so without this a row that scrolls
 * out of the window and back would show the hold again. Pages without the
 * lead are left as they are.
 */
export const clearLeadHeldBy =
  (
    leadId: string,
    organizationId: string
  ): ThunkAction<void, LeadListsRootState, unknown, UnknownAction> =>
  (dispatch, getState) => {
    const queries = (getState()[leadListsApi.reducerPath]?.queries ??
      {}) as Record<
      string,
      | {
          endpointName?: string;
          originalArgs?: Parameters<typeof updateLeadListLeadsCache>[1];
        }
      | undefined
    >;
    Object.values(queries).forEach((entry) => {
      if (entry?.endpointName !== 'getLeadListLeads') return;
      if (!entry.originalArgs) return;
      dispatch(
        leadListsApi.util.updateQueryData(
          'getLeadListLeads',
          entry.originalArgs,
          (draft) => {
            // Leads are shared across orgs and holds are per org, so only
            // this org's lists lose the hold.
            if (
              draft?.organization_id &&
              draft.organization_id !== organizationId
            )
              return;
            for (const lead of draft?.leads ?? []) {
              if (lead.id !== leadId || !lead.crm_sync_status) continue;
              for (const crm of Object.values(lead.crm_sync_status)) {
                if (crm && 'held_by' in crm) delete crm.held_by;
              }
            }
          }
        )
      );
    });
  };

export const invalidateLeadListLeadsCache = (
  dispatch: (action: unknown) => void,
  listId: string
) => {
  dispatch(
    leadListsApi.util.invalidateTags([{ type: 'LeadListLeads', id: listId }])
  );
};

export const invalidateLeadListCompaniesCache = (
  dispatch: (action: unknown) => void,
  listId: string
) => {
  dispatch(
    // The index's company_count changes with the sheet.
    leadListsApi.util.invalidateTags([
      { type: 'LeadListCompanies', id: listId },
      { type: 'LeadList', id: 'LIST' },
    ])
  );
};

export const {
  useGetLeadListsQuery,
  useLazyGetLeadListsQuery,
  useGetLeadListLeadsQuery,
  useLazyGetLeadListLeadsQuery,
  useGetLeadListLeadIdsQuery,
  useLazyGetLeadListLeadIdsQuery,
  useGetLeadRawEnrichmentQuery,
  useGetLeadListCompaniesQuery,
  useLazyGetLeadListCompaniesQuery,
  useCreateLeadListMutation,
  useGetOrCreateDefaultLeadListMutation,
  useEditLeadListMutation,
  useDeleteLeadListMutation,
  useBulkDeleteLeadListsMutation,
  useAssignLeadToListMutation,
  useBulkDeleteLeadsMutation,
  useMatchImportRowMutation,
  useMatchListLeadMutation,
  useBulkDeleteLeadsByCompanyMutation,
  useCopyLeadsToListMutation,
  useFilterLeadsMutation,
  useGetLeadListSharesQuery,
  useGetShareableUsersQuery,
  useCreateLeadListShareMutation,
  useUpdateLeadListShareMutation,
  useDeleteLeadListShareMutation,
  useToggleFavoriteMutation,
  useRecordOpenedMutation,
  useUploadCompaniesToListMutation,
  useAddCompanyToListMutation,
  useFindPersonalEmailMutation,
  useEnrichCompaniesInListMutation,
  useFindOpenJobsInListMutation,
  useFindReviewsInListMutation,
  useRemoveCompaniesFromListMutation,
} = leadListsApi;
